import type { SATTestInterventionArmTaskAttributes } from '@/server/models'

import { SQL, type SQLFragment } from '@/client/query'

import {
  type AssociationDefinitions,
  type IncludeAssociations,
  includeAssociationsToSql,
  makeQueryModelV2,
  type QueryOrder,
  queryOrderToSQL,
} from './common'
import type { SATUserType } from './constants'
import SATTestInterventionArmQ from './SATTestInterventionArmQ'
import SATTestUserInterventionArmQ from './SATTestUserInterventionArmQ'
import SATUserInterventionArmTaskSubmissionQ from './SATUserInterventionArmTaskSubmissionQ'
import SATUserQ from './SATUserQ'
import SeriesHostQ from './SeriesHostQ'
import SeriesQ from './SeriesQ'

const SATTestInterventionArmTaskQ = {
  ...makeQueryModelV2<SATTestInterventionArmTaskAttributes>('sat_test_intervention_arm_task')([
    'id',
    'name',
    'sat_test_intervention_arm_id',
    'link',
    'release_date',
    'due_date',
    'reminder_history',
    'for_learners',
    'created_at',
    'deleted_at',
    'updated_at',
  ]),

  associations: (): AssociationDefinitions => ({
    SATTestInterventionArm: {
      query: SATTestInterventionArmQ,
      foreignKey: SATTestInterventionArmQ.id,
      sourceKey: SATTestInterventionArmTaskQ.sat_test_intervention_arm_id,
    },
  }),

  where: (
    {
      id = null,
      satTestInterventionArmId,
      include = [],
      paranoid = true,
      order = null,
    }: {
      id?: number | null | SQLFragment<number>
      satTestInterventionArmId?: number | null | SQLFragment<number>
      include?: IncludeAssociations
      paranoid?: boolean
      order?: QueryOrder | null
    },
    customWhere: SQLFragment<unknown> | null = null
  ): SQLFragment<SATTestInterventionArmTaskAttributes> => SQL`
        FROM ${SATTestInterventionArmTaskQ}
        ${includeAssociationsToSql(SATTestInterventionArmTaskQ, include)}
        WHERE
            ${id != null ? SQL`${SATTestInterventionArmTaskQ.id} = ${id} AND` : SQL``}
            ${
              satTestInterventionArmId != null
                ? SQL`${SATTestInterventionArmTaskQ.sat_test_intervention_arm_id} = ${satTestInterventionArmId} AND`
                : SQL``
            }
            ${paranoid ? SQL`${SATTestInterventionArmTaskQ.deleted_at} IS NULL AND` : SQL``}
            ${customWhere != null ? customWhere : SQL`TRUE`}
        ${queryOrderToSQL(order)}
    `,

  getTasksForUser: ({
    taskId,
    userId,
    onlyShowActiveTasks = false,
    onlyShowIncompleteTasks = false,
    satUserType,
  }: {
    taskId?: number
    userId: number
    onlyShowActiveTasks?: boolean
    onlyShowIncompleteTasks?: boolean
    satUserType: SATUserType
  }): SQLFragment<unknown> =>
    SATTestInterventionArmTaskQ.where(
      {
        id: taskId,
        include: [{ association: 'SATTestInterventionArm', required: true }],
        order: [[SATTestInterventionArmTaskQ.release_date, 'asc']],
      },
      SQL`${SATTestInterventionArmTaskQ.release_date} < NOW() AND
      ${onlyShowActiveTasks ? SQL`${SATTestInterventionArmTaskQ.due_date} >= NOW()` : SQL`TRUE`} AND
      (EXISTS(SELECT 1 ${SATTestUserInterventionArmQ.where({
        include: [
          {
            association: 'SATTestUser',
            required: true,
            include: [
              {
                association: 'SATUser',
                required: true,
                where: SQL`(
                    (
                      ${SATTestInterventionArmTaskQ.for_learners} AND ${SATUserQ.user_id} IN (
                        SELECT ${SeriesHostQ.user_id} ${SeriesHostQ.where(
                          {},
                          SQL`${SeriesHostQ.series_id} IN (
                      SELECT ${SeriesQ.id} ${SeriesQ.getRegisteredSATBootcampSeries(userId)})`
                        )}
                      ) AND ${SATUserQ.type} = 'tutor'
                    ) OR (
                      NOT ${SATTestInterventionArmTaskQ.for_learners} AND
                        ${SATUserQ.user_id} = ${userId} AND ${SATUserQ.type} = ${satUserType}
                    )
                  )`,
              },
            ],
          },
        ],
        satTestInterventionArmId: SATTestInterventionArmTaskQ.sat_test_intervention_arm_id,
      })})) AND
      ${
        onlyShowIncompleteTasks
          ? SQL`NOT EXISTS (
        SELECT 1 ${SATUserInterventionArmTaskSubmissionQ.where({
          include: [
            { association: 'SATUser', required: true, where: SQL`${SATUserQ.user_id} = ${userId}` },
          ],
          satTestInterventionArmTaskId: SATTestInterventionArmTaskQ.id,
        })}
      )`
          : SQL`TRUE`
      }`
    ),
}

export default SATTestInterventionArmTaskQ
